import { useNavigate } from 'react-router-dom'
import { ChevronDown, LogOut, Moon, Settings, Sun, User, UserCog } from 'lucide-react'
import type { User as AuthUser } from '@/stores/auth-store'
import { DropdownMenu, DropdownMenuItem } from '@/components/ui/dropdown-menu'
import { HouseholdSelector } from '@/components/household/household-selector'

interface HeaderAccountControlsProps {
  user: AuthUser | null
  theme: 'light' | 'dark'
  onToggleTheme: () => void
  onLogout: () => void
  mobile?: boolean
}

export function HeaderAccountControls({
  user,
  theme,
  onToggleTheme,
  onLogout,
  mobile = false,
}: HeaderAccountControlsProps) {
  const navigate = useNavigate()

  if (!user) {
    return null
  }

  const accountTrigger = (
    <div className="flex min-w-0 items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-left shadow-sm transition hover:border-orange-200 hover:bg-orange-50/40">
      <div className="flex min-w-0 items-center gap-2">
        <User className="h-4 w-4 shrink-0 text-gray-500" />
        <span className="truncate text-sm font-medium text-gray-700">{user.name}</span>
      </div>
      <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" />
    </div>
  )

  if (mobile) {
    return (
      <div className="space-y-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
        <div className="border-b border-gray-200 pb-3">
          <HouseholdSelector className="w-full" />
        </div>

        <DropdownMenu className="w-full" triggerClassName="w-full" contentClassName="w-64 max-w-[calc(100vw-2rem)]" trigger={accountTrigger}>
          <DropdownMenuItem
            onClick={() => navigate('/profiel/bewerken')}
            icon={<UserCog className="h-4 w-4" />}
          >
            Profiel Bewerken
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate('/instellingen')}
            icon={<Settings className="h-4 w-4" />}
          >
            App Instellingen
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={onToggleTheme}
            icon={theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          >
            {theme === 'dark' ? 'Lichte modus' : 'Donkere modus'}
          </DropdownMenuItem>
          <div className="my-1 border-t border-gray-200"></div>
          <DropdownMenuItem
            onClick={onLogout}
            icon={<LogOut className="h-4 w-4" />}
            className="text-red-600 hover:bg-red-50"
          >
            Uitloggen
          </DropdownMenuItem>
        </DropdownMenu>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-wrap items-center justify-end gap-3">
      <HouseholdSelector className="w-full max-w-full lg:w-auto lg:max-w-[20rem]" />

      <DropdownMenu
        className="w-full min-w-0 lg:w-auto"
        triggerClassName="w-full min-w-0 lg:max-w-[14rem]"
        contentClassName="w-64"
        trigger={accountTrigger}
      >
        <DropdownMenuItem
          onClick={() => navigate('/profiel/bewerken')}
          icon={<UserCog className="h-4 w-4" />}
        >
          Profiel Bewerken
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/instellingen')}
          icon={<Settings className="h-4 w-4" />}
        >
          App Instellingen
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onToggleTheme}
          icon={theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        >
          {theme === 'dark' ? 'Lichte modus' : 'Donkere modus'}
        </DropdownMenuItem>
        <div className="my-1 border-t border-gray-200"></div>
        <DropdownMenuItem
          onClick={onLogout}
          icon={<LogOut className="h-4 w-4" />}
          className="text-red-600 hover:bg-red-50"
        >
          Uitloggen
        </DropdownMenuItem>
      </DropdownMenu>
    </div>
  )
}
